import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

import { API_BASE } from '../apiConfig';

const API = API_BASE;
const LiveModeContext = createContext();

export const useLiveMode = () => useContext(LiveModeContext);

export const LiveModeProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  // Fetch active session on mount
  useEffect(() => {
    const fetchActive = async () => {
      try {
        const r = await fetch(`${API}/teaching-sessions/active`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          if (data.session_id) setSession(data);
        }
      } catch (e) { /* not logged in yet */ }
      finally { setLoading(false); }
    };
    fetchActive();
  }, []);

  // Poll stats while session is active
  const fetchStats = useCallback(async () => {
    if (!session?.session_id) return;
    try {
      const r = await fetch(`${API}/teaching-sessions/stats/${session.session_id}`, { credentials: 'include' });
      if (r.ok) setStats(await r.json());
    } catch (e) { /* ignore */ }
  }, [session?.session_id]);

  useEffect(() => {
    if (session?.session_id && !session.ended_at) {
      fetchStats();
      pollRef.current = setInterval(fetchStats, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [session?.session_id, session?.ended_at, fetchStats]);

  const startSession = async (classId, period) => {
    const now = new Date();
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const r = await fetch(`${API}/teaching-sessions/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ class_id: classId, period, local_date: localDate }),
    });
    if (r.ok) {
      const data = await r.json();
      setSession(data);
      setStats(null);
      return data;
    }
    return null;
  };

  const endSession = async () => {
    if (!session?.session_id) return null;
    const r = await fetch(`${API}/teaching-sessions/end/${session.session_id}`, {
      method: 'POST',
      credentials: 'include',
    });
    if (r.ok) {
      const data = await r.json();
      setSession(null);
      setStats(null);
      return data;
    }
    return null;
  };

  const refreshStats = fetchStats;

  return (
    <LiveModeContext.Provider value={{ session, stats, loading, startSession, endSession, refreshStats }}>
      {children}
    </LiveModeContext.Provider>
  );
};
